/**
 * The Studio of the release kit (spec D24, D26): three formats, and the themes of each format. A
 * theme here is an ASSET TYPE (a story, a Canvas loop, ...): a brief for the asset director, never a
 * fixed design. The feeling of a release is its release theme (`releaseThemes.ts`); the technique of
 * one asset is its style (`assetStyles.ts`). The code name `StudioTheme` stays. Every asset type has
 * `availableFrom: 1` since the full videos and the lyric sync (spec
 * `2026-10-05-music24-full-videos-and-lyric-sync-design.md` §4.1): the Studio shows no "Coming soon".
 *
 * The file imports only `assetSpecs.ts`, which has no imports, so every
 * project can use it.
 */
import { ASSET_SPECS, type AssetSpecId } from './assetSpecs';

export const STUDIO_FORMATS = ['image', 'short', 'full'] as const;
export type StudioFormat = (typeof STUDIO_FORMATS)[number];

export const STUDIO_THEME_IDS = [
  // image
  'story',
  'feedPost',
  'banners',
  'spotifyHeader',
  'thumbnail',
  'coverSizes',
  'lyricCard',
  'linkPreview',
  // short
  'aiReel',
  'artworkInMotion',
  'countdown',
  'canvasLoop',
  'lyricMoment',
  'motionArtwork',
  // full
  'lyricVideo',
  'visualizer',
  'musicVideo',
] as const;
export type StudioThemeId = (typeof STUDIO_THEME_IDS)[number];

export type StudioThemeFamily =
  | 'lyrics'
  | 'artwork'
  | 'aiFootage'
  | 'releaseMoments'
  | 'serviceLoops'
  | 'profileImages';
export type KitShape = '9:16' | '1:1' | '4:5' | '16:9' | '3:1' | '1.91:1';

/** One file that a theme makes. `variant` names it inside the theme (for example `youtube` in banners). */
export interface StudioAssetOutput {
  variant: string;
  specId: AssetSpecId;
}

export type StudioMoment = 'preSave' | 'outNow' | 'lyricLine' | 'announce';
export const STUDIO_MOMENTS: ReadonlyArray<StudioMoment> = ['preSave', 'outNow', 'lyricLine', 'announce'];

export type StudioLayer = 'artwork' | 'bankClip' | 'audioReactive' | 'text' | 'logo';
/**
 * The step-3 fields of the Studio. `trim`: the optional trim of a full video (`params.trim`).
 * `lines`: the first and the last lyric line of a lyric moment (`params.lineFrom`, `params.lineTo`).
 * `headerSource`: the picture of the Spotify header and its position (`spotifyHeader.ts`).
 */
export type StudioDetail =
  | 'moment'
  | 'lyricLine'
  | 'window'
  | 'length'
  | 'shape'
  | 'reelForm'
  | 'trim'
  | 'lines'
  | 'headerSource';

export interface StudioTheme {
  id: StudioThemeId;
  format: StudioFormat;
  family: StudioThemeFamily;
  /** Credits per output video; images are 0. `creditsPerRun` replaces it for a whole run. */
  credits: 0 | 1 | 3;
  /**
   * The credits of one run, for all its video files together. Absent: `credits` for each video
   * file. The motion artwork costs 3 for the pair of files (owner decision 2026-10-05). Read the
   * charge with `runCredits` / `outputCredits`, never from this field alone.
   */
  creditsPerRun?: number;
  /** The first release-kit phase that makes it. Every asset type has 1 now. */
  availableFrom: 1 | 2 | 3 | 4;
  /** The files one run makes. */
  outputs: StudioAssetOutput[];
  /**
   * What the director must make (D24). Read by `buildAssetDirectorPrompt`, never shown to users.
   * The styles that the asset type offers are in `ASSET_TYPE_STYLES` (`assetStyles.ts`).
   */
  brief: { purpose: string; allowedLayers: StudioLayer[]; rules: string[] };
  /** The step-3 fields the Studio shows. */
  details: StudioDetail[];
  /** Shown only when the workspace has `canUseAiReels` (D17). */
  needsAiFootage: boolean;
  /** Needs a ready master (it reads the song). Images with no audio still read the analysis when one exists. */
  needsMaster: boolean;
  /**
   * Needs the lyrics of the song and their timing (`lyricTiming.ts`): the lyric video and the lyric
   * moment. Create answers 400 `M24_NO_LYRICS` for a song with no lyrics, and the plan waits while
   * the timing syncs. The music video is false: it shows captions only when the timing is synced.
   */
  needsLyrics: boolean;
  /**
   * Made in its own editor, never in one shot (the music video, spec 2026-10-09 D11): "Make the
   * kit", "Make them again" and the rollout leave it out, and the Studio opens the editor.
   */
  madeInEditor?: boolean;
}

/** The variants of the countdown: 7, 3 and 1 days before the release. */
export const COUNTDOWN_VARIANTS = ['d7', 'd3', 'd1'] as const;
export type CountdownVariant = (typeof COUNTDOWN_VARIANTS)[number];

/**
 * The story variant of each single-file moment (review I4). Each moment is its own file: a new
 * out-now story never replaces the pre-save story, and a lyric story can be made while the
 * pre-save story renders. The moment `announce` makes the countdown stills (`COUNTDOWN_VARIANTS`).
 */
export const STORY_MOMENT_VARIANTS = { preSave: 'preSave', outNow: 'outNow', lyricLine: 'lyric' } as const;
export type StoryMomentVariant = (typeof STORY_MOMENT_VARIANTS)[keyof typeof STORY_MOMENT_VARIANTS];

/** The moment of a story variant; null for a variant that is not a story moment. */
export function storyMomentOfVariant(variant: string): StudioMoment | null {
  if ((COUNTDOWN_VARIANTS as readonly string[]).includes(variant)) {
    return 'announce';
  }
  const entry = Object.entries(STORY_MOMENT_VARIANTS).find(([, value]) => value === variant);
  return entry === undefined ? null : (entry[0] as StudioMoment);
}

/**
 * The feed post variant of each moment. Each moment is its own file, as for a story: a new
 * out-now post never replaces the announce post. The shape picks the spec, not the variant, so a
 * square post replaces the portrait post of the same moment.
 */
export const FEED_MOMENT_VARIANTS = {
  preSave: 'preSave',
  outNow: 'outNow',
  lyricLine: 'lyric',
  announce: 'announce',
} as const;
export type FeedMomentVariant = (typeof FEED_MOMENT_VARIANTS)[keyof typeof FEED_MOMENT_VARIANTS];

/**
 * The videos that get a YouTube thumbnail (owner answer 1 of page i): an audio upload with the
 * still cover (the master, no kit video) and the three full videos of the kit. One thumbnail per
 * video: each is its own variant, so a lyric video thumbnail never replaces the audio thumbnail.
 * The audio thumbnail keeps the old variant `youtube`.
 */
export const THUMBNAIL_VIDEOS = ['audio', 'lyricVideo', 'visualizer', 'musicVideo'] as const;
export type ThumbnailVideo = (typeof THUMBNAIL_VIDEOS)[number];

export const THUMBNAIL_VIDEO_VARIANTS: Readonly<Record<ThumbnailVideo, string>> = {
  audio: 'youtube',
  lyricVideo: 'lyricVideo',
  visualizer: 'visualizer',
  musicVideo: 'musicVideo',
};

export function isThumbnailVideo(value: unknown): value is ThumbnailVideo {
  return typeof value === 'string' && (THUMBNAIL_VIDEOS as readonly string[]).includes(value);
}

/** The video of a thumbnail variant; an unknown variant is the audio thumbnail. */
export function thumbnailVideoOfVariant(variant: string): ThumbnailVideo {
  const entry = Object.entries(THUMBNAIL_VIDEO_VARIANTS).find(([, value]) => value === variant);
  return entry === undefined ? 'audio' : (entry[0] as ThumbnailVideo);
}

/**
 * The moment that the variant of a kit asset names: a story (`STORY_MOMENT_VARIANTS`, the
 * countdown) or a feed post (`FEED_MOMENT_VARIANTS`). Null for a theme with no moment per variant,
 * and for an unknown variant.
 */
export function momentOfVariant(themeId: string, variant: string): StudioMoment | null {
  if (themeId === 'story') {
    return storyMomentOfVariant(variant);
  }
  if (themeId === 'feedPost') {
    const entry = Object.entries(FEED_MOMENT_VARIANTS).find(([, value]) => value === variant);
    return entry === undefined ? null : (entry[0] as StudioMoment);
  }
  return null;
}

const IMAGE_TEXT_RULES = [
  'Use the song title and the artist name exactly as given.',
  'Keep text inside the safe area of the destination.',
  'Use only the colours of the artwork palette.',
];

export const STUDIO_THEMES: Readonly<Record<StudioThemeId, StudioTheme>> = {
  story: {
    id: 'story',
    format: 'image',
    family: 'releaseMoments',
    credits: 0,
    availableFrom: 1,
    // The default moment is out now; `themeOutputs` picks the variant of the chosen moment.
    outputs: [{ variant: STORY_MOMENT_VARIANTS.outNow, specId: 'story916' }],
    brief: {
      purpose:
        'A full-screen story image for one release moment: pre-save, out now, a lyric line or a countdown day. One theme frame; the moment changes only the hero and the call to action.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'Keep the top 250 px and the bottom 340 px free of text, cover and logo: the app covers them. Everything sits in the safe column, y 270 to 1560.',
        'Code lays out the Story: the hero of the moment (the date, "Out now", the lyric or the day number), the cover, the title one time, the artist, and the logo first when the kit has one.',
        'One call to action that matches the moment: code writes the pill ("Pre-save now" or "Listen now") and the SmartLink address of the song under it.',
      ],
    },
    details: ['moment', 'lyricLine'],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  feedPost: {
    id: 'feedPost',
    format: 'image',
    family: 'releaseMoments',
    credits: 0,
    availableFrom: 1,
    // The default moment is out now; `themeOutputs` picks the variant of the chosen moment.
    outputs: [{ variant: FEED_MOMENT_VARIANTS.outNow, specId: 'feed45' }],
    brief: {
      purpose:
        'A feed post image for one release moment (pre-save, out now, a lyric line or announce), in 4:5 or 1:1. The Story system fitted to the feed: one theme frame; the moment changes only the hero and the call to action.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'The hero depends on the moment: the release date and the cover (pre-save), the cover and "Out now" (out now), the lyric (lyric line), "New single" and "Out {weekday} {date}" (announce).',
        'Code lays out the post in the safe column (x 84 to 996, y 72 to 1278 of a 4:5 post; the 3:4 profile grid crop keeps it): the cover in every post, the title one time, the artist, and the logo first when the kit has one.',
        'One call to action that matches the moment: code writes the pill ("Pre-save now", "Listen now" or "Coming soon") and "Link in bio" with the SmartLink address of the song under it.',
      ],
    },
    details: ['moment', 'lyricLine', 'shape'],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  banners: {
    id: 'banners',
    format: 'image',
    family: 'profileImages',
    credits: 0,
    availableFrom: 1,
    outputs: [
      { variant: 'youtube', specId: 'bannerYoutube' },
      { variant: 'x', specId: 'bannerX' },
      { variant: 'facebook', specId: 'bannerFacebook' },
    ],
    brief: {
      purpose:
        'One evergreen "new single" profile banner for each of YouTube, X and Facebook: the cover, the title, the artist, the status and the SmartLink.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'Code lays out the banner in the content box of the site: the cover in the frame of the theme, then the status, the title one time, the artist and "Listen on" with the SmartLink (no URL on Facebook).',
        'The status is "Out {weekday} {date}" before the release day and "Out now" from the release day; on the release day the kit makes the "Out now" banners again.',
        'Leave the bottom-left corner of the X and the Facebook banner calm: the profile photo covers it.',
      ],
    },
    details: [],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  spotifyHeader: {
    id: 'spotifyHeader',
    format: 'image',
    family: 'profileImages',
    credits: 0,
    availableFrom: 1,
    outputs: [{ variant: 'spotify', specId: 'spotifyHeader' }],
    brief: {
      purpose:
        'The header image of the Spotify artist page (spec 2026-10-17): a picture with no text and no logo. Code makes it from the picked source; no director.',
      allowedLayers: ['artwork'],
      rules: [
        'No text and no logos (the Spotify rule). A Free plan adds the Music24 watermark (owner rule).',
        'Keep the subject in the subject box of `spotifyHeader.ts`: Spotify draws the photo and the name bottom left and crops the sides on a phone.',
      ],
    },
    details: ['headerSource'],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  thumbnail: {
    id: 'thumbnail',
    format: 'image',
    family: 'profileImages',
    credits: 0,
    availableFrom: 1,
    outputs: [{ variant: 'youtube', specId: 'thumbnailYoutube' }],
    brief: {
      purpose:
        'A YouTube thumbnail for one video of the song (the audio upload, the lyric video, the visualizer or the music video) that reads at a small size.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'Code lays out the thumbnail: the cover in the frame of the theme on the left, then the video label ("Official audio", "Lyric video", "Visualizer", "Official video"), the title one time and the artist. A title on two lines drops the artist.',
        'At most four words of text, large and with strong contrast. No status and no URL: the SmartLink goes in the video description.',
        'Keep the bottom-right corner free: YouTube shows the length there.',
      ],
    },
    details: [],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  coverSizes: {
    id: 'coverSizes',
    format: 'image',
    family: 'artwork',
    credits: 0,
    availableFrom: 1,
    outputs: [
      { variant: '3000', specId: 'cover3000' },
      { variant: '1400', specId: 'cover1400' },
      { variant: '640', specId: 'cover640' },
    ],
    brief: {
      purpose: 'The cover art at the sizes that distributors and stores ask for. A plain resize, no director.',
      allowedLayers: ['artwork'],
      rules: [
        'No text and no logos. A Free plan adds the Music24 watermark (owner rule).',
        'Never crop or change the artwork.',
      ],
    },
    details: [],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  lyricCard: {
    id: 'lyricCard',
    format: 'image',
    family: 'lyrics',
    credits: 0,
    availableFrom: 1,
    outputs: [{ variant: 'square', specId: 'lyricCard11' }],
    brief: {
      purpose:
        'A square card with one lyric line of the song: the lyric, the cover with the title, a pill by date and "Link in bio" with the SmartLink.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'The lyric line is the hero; quote it exactly, in its own case, with no change.',
        'Code lays out the card from the texts; pick the texts, not the positions.',
      ],
    },
    details: ['lyricLine'],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  linkPreview: {
    id: 'linkPreview',
    format: 'image',
    family: 'profileImages',
    credits: 0,
    availableFrom: 1,
    outputs: [{ variant: 'og', specId: 'linkPreview' }],
    brief: {
      purpose:
        'The preview image that a chat or a social site shows for the SmartLink of the song: "New single", the title, the artist, the cover and the status badge.',
      allowedLayers: ['artwork', 'text', 'logo'],
      rules: [
        ...IMAGE_TEXT_RULES,
        'Code lays out the link preview: "New single", the title and the artist on the left, the cover in the centre square (some apps crop the preview to 1:1), the status badge on the right. No URL, no logo.',
        'The badge is "Out {date} {weekday}" before the release day, "Out Now" from the release day and "Coming Soon" with no date; on the release day the kit makes the link preview again.',
      ],
    },
    details: [],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  aiReel: {
    id: 'aiReel',
    format: 'short',
    family: 'aiFootage',
    credits: 1,
    availableFrom: 1,
    // The AI reel keeps `ReelModel` and its review flow (Q3), so it has no kit output.
    outputs: [],
    brief: {
      purpose: 'An AI reel made by the reel pipeline. The asset director does not plan it.',
      allowedLayers: ['bankClip', 'audioReactive', 'text'],
      rules: [],
    },
    details: ['reelForm'],
    needsAiFootage: true,
    needsMaster: true,
    needsLyrics: false,
  },
  artworkInMotion: {
    id: 'artworkInMotion',
    format: 'short',
    family: 'artwork',
    credits: 1,
    availableFrom: 1,
    outputs: [{ variant: 'video', specId: 'short916' }],
    brief: {
      purpose: 'A short video of the cover art that moves with the chosen part of the song.',
      // The design offers the AI footage style here too, so the type allows bank clips.
      allowedLayers: ['artwork', 'bankClip', 'audioReactive', 'text', 'logo'],
      rules: [
        'Use only the artwork, or bank clips when the style is AI footage.',
        'An artwork segment keeps the artwork recognisable.',
        'Motion follows the beat and the energy of the song.',
        'Code lays out the cover, the bars, the title line and the call to action; the plan picks the window and the cover crop.',
      ],
    },
    // One length (15 s) and no moment (owner answers 2026-10-08, page q): the pill follows the date.
    details: ['shape'],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: false,
  },
  countdown: {
    id: 'countdown',
    format: 'short',
    family: 'releaseMoments',
    credits: 1,
    availableFrom: 1,
    outputs: COUNTDOWN_VARIANTS.map((variant) => ({ variant, specId: 'short916' as const })),
    brief: {
      purpose: 'Three short videos that count down to the release: 7, 3 and 1 days to go.',
      allowedLayers: ['artwork', 'audioReactive', 'text', 'logo'],
      rules: [
        'Show the number of days left large and clear.',
        'The three videos share one look and differ only in the count.',
        'End on the release date and a pre-save call to action.',
      ],
    },
    details: [],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: false,
  },
  canvasLoop: {
    id: 'canvasLoop',
    format: 'short',
    family: 'serviceLoops',
    credits: 1,
    availableFrom: 1,
    outputs: [{ variant: 'canvas', specId: 'canvas' }],
    brief: {
      purpose:
        'A silent loop of four bars (3–8 s) behind the song in a streaming app. The motion follows the beat of the chosen part of the song.',
      allowedLayers: ['artwork', 'bankClip', 'audioReactive'],
      rules: [
        'No text, no logos, no links.',
        'The last frame joins the first frame with no jump.',
        'Keep the bottom third calm: the player covers it.',
        'Use only the artwork, or bank clips when the style is AI footage.',
        'No rapid cuts and no flashing.',
      ],
    },
    // Four bars of the song (owner answer 2026-10-08, page s): the Studio has no length choice.
    details: ['window'],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: false,
  },
  lyricMoment: {
    id: 'lyricMoment',
    format: 'short',
    family: 'lyrics',
    credits: 1,
    availableFrom: 1,
    outputs: [{ variant: 'video', specId: 'short916' }],
    brief: {
      purpose: 'A short video of the lyric lines that the user picks, with each word at its sung time.',
      allowedLayers: ['artwork', 'bankClip', 'audioReactive', 'text', 'logo'],
      rules: [
        'Show each word at its sung time.',
        'The lyric text comes from the synced timing. Never write a lyric line.',
      ],
    },
    details: ['lines', 'length'],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: true,
  },
  motionArtwork: {
    id: 'motionArtwork',
    format: 'short',
    family: 'serviceLoops',
    credits: 3,
    creditsPerRun: 3,
    availableFrom: 1,
    outputs: [
      { variant: 'square', specId: 'motionSquare' },
      { variant: 'tall', specId: 'motionTall' },
    ],
    brief: {
      purpose: 'Apple Music motion artwork: a slow, silent loop of the cover art, square and tall.',
      allowedLayers: ['artwork'],
      rules: [
        'No text, no logos.',
        'The last frame joins the first frame with no jump.',
        'The first frame is the cover with no change: no texture, no wash and no frame of the theme.',
      ],
    },
    details: ['length'],
    needsAiFootage: false,
    needsMaster: false,
    needsLyrics: false,
  },
  lyricVideo: {
    id: 'lyricVideo',
    format: 'full',
    family: 'lyrics',
    credits: 3,
    availableFrom: 1,
    outputs: [{ variant: 'video', specId: 'full169' }],
    brief: {
      purpose: 'A full-song lyric video for YouTube.',
      allowedLayers: ['artwork', 'bankClip', 'audioReactive', 'text', 'logo'],
      rules: [
        'Show each line at its sung time.',
        'The lyric text comes from the synced timing. Never write a lyric line.',
      ],
    },
    details: ['trim'],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: true,
  },
  visualizer: {
    id: 'visualizer',
    format: 'full',
    family: 'artwork',
    credits: 3,
    availableFrom: 1,
    outputs: [{ variant: 'video', specId: 'full169' }],
    brief: {
      purpose: 'A full-song visualizer for YouTube: the artwork and motion that follow the song.',
      allowedLayers: ['artwork', 'audioReactive', 'text', 'logo'],
      rules: [
        'The artwork stays recognisable in every frame.',
        'On the wide frame the cover is a card, never the full frame.',
        'Motion follows the sections of the song.',
      ],
    },
    details: ['trim'],
    needsAiFootage: false,
    needsMaster: true,
    needsLyrics: false,
  },
  musicVideo: {
    id: 'musicVideo',
    format: 'full',
    family: 'aiFootage',
    credits: 3,
    availableFrom: 1,
    outputs: [{ variant: 'video', specId: 'full169' }],
    brief: {
      purpose: 'A full-song music video cut from the clips of the bank on the beat.',
      allowedLayers: ['bankClip', 'artwork', 'audioReactive', 'text', 'logo'],
      rules: [
        'Cut on the beat.',
        'Match the footage to the mood of each section.',
        'The captions come from the synced timing. Never write a lyric line.',
      ],
    },
    details: ['trim'],
    needsAiFootage: true,
    needsMaster: true,
    needsLyrics: false,
    madeInEditor: true,
  },
};

/** True for an asset type that its own editor makes (the music video). */
export function isMadeInEditor(themeId: StudioThemeId): boolean {
  return STUDIO_THEMES[themeId].madeInEditor === true;
}

export function isStudioThemeId(value: unknown): value is StudioThemeId {
  return typeof value === 'string' && (STUDIO_THEME_IDS as readonly string[]).includes(value);
}

/** The themes of one format, in table order. */
export function themesOfFormat(format: StudioFormat): StudioTheme[] {
  return STUDIO_THEME_IDS.map((id) => STUDIO_THEMES[id]).filter((theme) => theme.format === format);
}

/**
 * True when the release-kit phase `phase` (default 1) makes the theme. Every asset type has
 * `availableFrom: 1`, so this is always true for phase 1; a follow-up removes it (spec §4.1).
 */
export function isThemeAvailable(id: StudioThemeId, phase = 1): boolean {
  return STUDIO_THEMES[id].availableFrom <= phase;
}

/**
 * The files one run of a theme makes, after the step-3 choices. A `story` makes one file per
 * moment (`preSave`, `outNow`, `lyric`; review I4), and with the moment `announce` the three
 * countdown stills (`d7`, `d3`, `d1`). A `feedPost` makes one file per moment
 * (`FEED_MOMENT_VARIANTS`). A `feedPost` or an `artworkInMotion` with a shape picks the spec of
 * that shape.
 */
export function themeOutputs(
  id: StudioThemeId,
  options: { moment?: StudioMoment; shape?: KitShape; video?: ThumbnailVideo } = {}
): StudioAssetOutput[] {
  const { moment, shape, video } = options;
  if (id === 'thumbnail' && video !== undefined) {
    return [{ variant: THUMBNAIL_VIDEO_VARIANTS[video], specId: 'thumbnailYoutube' }];
  }
  if (id === 'story' && moment !== undefined) {
    return moment === 'announce'
      ? COUNTDOWN_VARIANTS.map((variant) => ({ variant, specId: 'story916' as const }))
      : [{ variant: STORY_MOMENT_VARIANTS[moment], specId: 'story916' }];
  }
  if (id === 'feedPost') {
    return [{ variant: FEED_MOMENT_VARIANTS[moment ?? 'outNow'], specId: shape === '1:1' ? 'feed11' : 'feed45' }];
  }
  if (id === 'artworkInMotion' && shape === '1:1') {
    return [{ variant: 'video', specId: 'short11' }];
  }
  if (id === 'artworkInMotion' && shape === '4:5') {
    return [{ variant: 'video', specId: 'short45' }];
  }
  return STUDIO_THEMES[id].outputs.map((output) => ({ ...output }));
}

/** True for the asset types of the Full format: a video of the whole song (or of its trim). */
export function isFullVideoTheme(id: StudioThemeId): boolean {
  return STUDIO_THEMES[id].format === 'full';
}

/**
 * True for the asset types that need Pro or higher (or the trial): the full videos and the motion
 * artwork (pricing rethink 2026-10-08). The capability `canRenderFullSongVideos` opens them. The
 * server refuses them on Free and Artist with one 402 (`createKitAssets`), and the Studio and the
 * song page show the upgrade state.
 */
export function needsFullVideoPlan(id: StudioThemeId): boolean {
  return isFullVideoTheme(id) || id === 'motionArtwork';
}

/** The lengths of a lyric moment (`params.lengthSec`), in seconds. */
export const LYRIC_MOMENT_LENGTHS = [15, 30] as const;
export type LyricMomentLengthSec = (typeof LYRIC_MOMENT_LENGTHS)[number];

/** The lengths of the motion artwork (`params.lengthSec`), in seconds, inside 15..30 s of its specs. */
export const MOTION_ARTWORK_LENGTHS = [15, 20, 30] as const;
export type MotionArtworkLengthSec = (typeof MOTION_ARTWORK_LENGTHS)[number];
export const MOTION_ARTWORK_DEFAULT_LENGTH_SEC: MotionArtworkLengthSec = 20;

/**
 * The credits of each file of one run, in the order of `outputs` (the files of `themeOutputs`,
 * after the countdown filter). An image is 0. A video is `credits`, except for an asset type with
 * `creditsPerRun`: the run charge is split over its video files, the remainder on the first files
 * (the motion artwork pair: square 2, tall 1). So each file gives back its own share when it fails,
 * and the pair costs `creditsPerRun` together. The only place that computes the credits of a kit
 * asset: the server charges with it and the Studio shows it. Pure.
 */
export function outputCredits(id: StudioThemeId, outputs: readonly StudioAssetOutput[]): number[] {
  const theme = STUDIO_THEMES[id];
  const isVideo = outputs.map((output) => ASSET_SPECS[output.specId].kind === 'video');
  if (theme.creditsPerRun === undefined) {
    return isVideo.map((video) => (video ? theme.credits : 0));
  }
  const videoCount = isVideo.filter(Boolean).length;
  if (videoCount === 0) {
    return isVideo.map(() => 0);
  }
  const share = Math.floor(theme.creditsPerRun / videoCount);
  let remainder = theme.creditsPerRun - share * videoCount;
  return isVideo.map((video) => {
    if (!video) {
      return 0;
    }
    if (remainder > 0) {
      remainder -= 1;
      return share + 1;
    }
    return share;
  });
}

/**
 * The credits of one file made again alone ("Make them again" of one asset): its share of
 * `outputCredits` over the outputs of the theme table, found by the variant; else `credits` for a
 * video and 0 for an image. Pure.
 */
export function fileCredits(id: StudioThemeId, output: StudioAssetOutput): number {
  const { outputs } = STUDIO_THEMES[id];
  const index = outputs.findIndex((candidate) => candidate.variant === output.variant);
  if (STUDIO_THEMES[id].creditsPerRun !== undefined && index >= 0) {
    return outputCredits(id, outputs)[index] ?? 0;
  }
  return outputCredits(id, [output])[0] ?? 0;
}

/** The credits of one run of an asset type with these files: the sum of `outputCredits`. Pure. */
export function runCredits(id: StudioThemeId, outputs: readonly StudioAssetOutput[]): number {
  return outputCredits(id, outputs).reduce((sum, credits) => sum + credits, 0);
}

/** Whole days from `today` to `releaseDate` (both `YYYY-MM-DD`, or ISO strings that start with it). */
function daysUntil(releaseDate: string, today: string): number | null {
  const release = Date.parse(`${releaseDate.slice(0, 10)}T00:00:00Z`);
  const now = Date.parse(`${today.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(release) || Number.isNaN(now)) {
    return null;
  }
  return Math.round((release - now) / 86_400_000);
}

/** The days before the release from which "Make the kit" adds the countdown stills. */
export const COUNTDOWN_MIN_DAYS = 7;

/**
 * The set "Make the kit" makes (Q7). `releaseDate` null or past drops the countdown stills and the
 * pre-save story. No AI reels: each reel needs its own review. No asset type of an editor
 * (`madeInEditor`, the music video): the artist makes it in the editor.
 */
export function defaultKitThemes(input: {
  releaseDate: string | null;
  today: string;
}): Array<{ themeId: StudioThemeId; moment?: StudioMoment }> {
  const days = input.releaseDate === null ? null : daysUntil(input.releaseDate, input.today);
  const themes: Array<{ themeId: StudioThemeId; moment?: StudioMoment }> = [];

  if (days !== null && days > 0) {
    themes.push({ themeId: 'story', moment: 'preSave' });
    if (days >= COUNTDOWN_MIN_DAYS) {
      themes.push({ themeId: 'story', moment: 'announce' });
    }
  } else {
    themes.push({ themeId: 'story', moment: 'outNow' });
  }

  themes.push(
    { themeId: 'feedPost', moment: 'outNow' },
    { themeId: 'coverSizes' },
    { themeId: 'linkPreview' },
    { themeId: 'banners' },
    { themeId: 'spotifyHeader' },
    { themeId: 'thumbnail' },
    { themeId: 'canvasLoop' }
  );
  return themes.filter((theme) => !isMadeInEditor(theme.themeId));
}
